import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Clock,
  UserPlus,
  MessageSquare,
  CheckSquare,
  TabletSmartphone,
  CalendarPlus,
  ChevronDown,
  DollarSign,
} from "lucide-react";
import AddStudentDialog from "@/components/students/AddStudentDialog";
import QuickChargeModal from "@/components/billing/QuickChargeModal";
import { toast } from "sonner";

/**
 * Daily Actions - Contextual shortcuts for common daily tasks
 * These are NOT duplicates of sidebar navigation but time-saving actions
 * focused on "today's" workflow and frequent operations.
 */
export default function QuickActions() {
  const navigate = useNavigate();
  const [showAddWalkin, setShowAddWalkin] = useState(false);
  const [showQuickCharge, setShowQuickCharge] = useState(false);

  const handleTodaysClasses = () => {
    navigate("/admin/schedule?view=today");
  };

  const handleTakeAttendance = () => {
    navigate("/admin/classes?tab=attendance");
  };

  const handleAvailableMakeups = () => {
    navigate("/admin/makeup-classes", { state: { activeTab: 3 } }); // 4th tab (0-indexed)
  };

  const handleSendMessage = () => {
    navigate("/admin/messages?action=compose");
  };

  const handleLaunchKiosk = () => {
    window.open("/check-in", "_blank", "width=1200,height=800");
  };

  const handleWalkinAdded = () => {
    setShowAddWalkin(false);
    toast.success("Walk-in added successfully!");
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 flex-wrap">
        {/* Today's Context Actions */}
        <Button 
          size="sm" 
          onClick={handleTodaysClasses}
          className="gap-2"
        >
          <Clock className="h-4 w-4" />
          Today's Classes
        </Button>

        <Button 
          variant="outline" 
          size="sm"
          onClick={handleTakeAttendance}
          className="gap-2"
        >
          <CheckSquare className="h-4 w-4" />
          Take Attendance
        </Button>

        <Button 
          variant="outline" 
          size="sm"
          onClick={() => setShowAddWalkin(true)}
          className="gap-2"
        >
          <UserPlus className="h-4 w-4" />
          Add Walk-in
        </Button>

        <Button 
          variant="outline" 
          size="sm"
          onClick={() => setShowQuickCharge(true)}
          className="gap-2 text-green-600 hover:text-green-700 border-green-200 hover:border-green-300"
        >
          <DollarSign className="h-4 w-4" />
          Quick Charge
        </Button>

        {/* More Daily Actions Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-1">
              More
              <ChevronDown className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem onClick={handleSendMessage}>
              <MessageSquare className="mr-2 h-4 w-4" />
              Send Quick Message
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleAvailableMakeups}>
              <CalendarPlus className="mr-2 h-4 w-4" />
              Available Makeups
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLaunchKiosk}>
              <TabletSmartphone className="mr-2 h-4 w-4" />
              Check-in Kiosk
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
          </div>
        </CardContent>
      </Card>

      {/* Dialogs */}
      <AddStudentDialog
        open={showAddWalkin}
        onOpenChange={setShowAddWalkin}
        onStudentAdded={handleWalkinAdded}
      />
      
      <QuickChargeModal
        open={showQuickCharge}
        onOpenChange={setShowQuickCharge}
      />
    </>
  );
}